import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { supabase } from "../lib/supabase.js";
import { api, setApiToken } from "../lib/api.js";
import type { User, Session } from "@supabase/supabase-js";
import type { UserPreferences } from "../store/types.js";

interface CreditBalance {
  balance: number;
  held: number;
  available: number;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  preferences: UserPreferences;
  defaultApiKey: string | null;
  handle: string | null;
  credits: CreditBalance | null;
  refreshCredits: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (
    email: string,
    password: string,
    displayName: string
  ) => Promise<void>;
  signOut: () => Promise<void>;
  updatePreferences: (
    prefs: UserPreferences | ((current: UserPreferences) => UserPreferences)
  ) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState<UserPreferences>({});
  const [defaultApiKey, setDefaultApiKey] = useState<string | null>(null);
  const [handle, setHandle] = useState<string | null>(null);
  const [credits, setCredits] = useState<CreditBalance | null>(null);

  const preferencesRef = useRef<UserPreferences>({});
  const persistedPreferencesRef = useRef<UserPreferences>({});
  const preferencesUserIdRef = useRef<string | null>(null);
  const preferencesLoadRef = useRef<{
    userId: string;
    promise: Promise<UserPreferences>;
  } | null>(null);
  const preferencesSaveQueueRef = useRef<UserPreferences | null>(null);
  const preferencesSaveLoopRef = useRef<Promise<void> | null>(null);
  const sessionRequestIdRef = useRef(0);

  const applyPreferences = useCallback((next: UserPreferences) => {
    preferencesRef.current = next;
    setPreferences(next);
  }, []);

  const loadPreferences = useCallback((sessionUser: User) => {
    const existing = preferencesLoadRef.current;
    if (existing?.userId === sessionUser.id) {
      return existing.promise;
    }

    const fallback =
      (sessionUser.user_metadata?.preferences as UserPreferences | undefined) || {};
    const promise = api
      .get<UserPreferences>("/api/user/preferences")
      .catch(() => fallback)
      .finally(() => {
        if (preferencesLoadRef.current?.promise === promise) {
          preferencesLoadRef.current = null;
        }
      });

    preferencesLoadRef.current = {
      userId: sessionUser.id,
      promise,
    };

    return promise;
  }, []);

  const flushPreferenceSaves = useCallback(async () => {
    if (preferencesSaveLoopRef.current) {
      return preferencesSaveLoopRef.current;
    }

    const loop = (async () => {
      while (preferencesSaveQueueRef.current) {
        const next = preferencesSaveQueueRef.current;
        const saveUserId = preferencesUserIdRef.current;
        if (!saveUserId) {
          preferencesSaveQueueRef.current = null;
          return;
        }
        preferencesSaveQueueRef.current = null;
        try {
          await api.put("/api/user/preferences", next, { keepalive: true });
        } catch (error) {
          if (!preferencesSaveQueueRef.current) {
            preferencesSaveQueueRef.current = next;
          }
          throw error;
        }
        if (preferencesUserIdRef.current !== saveUserId) {
          return;
        }
        persistedPreferencesRef.current = next;
      }
    })().finally(() => {
      preferencesSaveLoopRef.current = null;
    });

    preferencesSaveLoopRef.current = loop;
    return loop;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const handleSession = async (session: Session | null) => {
      const requestId = ++sessionRequestIdRef.current;
      setApiToken(session?.access_token ?? "");
      setSession(session);
      setUser(session?.user ?? null);

      if (!session?.user) {
        setDefaultApiKey(null);
        setHandle(null);
        setCredits(null);
        persistedPreferencesRef.current = {};
        applyPreferences({});
        preferencesUserIdRef.current = null;
        preferencesLoadRef.current = null;
        preferencesSaveQueueRef.current = null;
        if (!cancelled && requestId === sessionRequestIdRef.current) {
          setLoading(false);
        }
        return;
      }

      if (preferencesUserIdRef.current !== session.user.id) {
        setDefaultApiKey(null);
        setHandle(null);
        setCredits(null);
        setLoading(true);
        const serverPreferences = await loadPreferences(session.user);
        if (cancelled || requestId !== sessionRequestIdRef.current) {
          return;
        }
        preferencesUserIdRef.current = session.user.id;
        persistedPreferencesRef.current = serverPreferences;
        applyPreferences(serverPreferences);
      }

      api
        .post<{ key: string; created: boolean }>("/api/keys/ensure-default")
        .then((r) => {
          if (!cancelled && requestId === sessionRequestIdRef.current) {
            setDefaultApiKey(r.key);
          }
        })
        .catch(() => {});
      api
        .get<{ handle: string | null }>("/api/user/profile")
        .then((r) => {
          if (!cancelled && requestId === sessionRequestIdRef.current) {
            setHandle(r.handle);
          }
        })
        .catch(() => {});
      api
        .get<CreditBalance>("/api/credits/balance")
        .then((r) => {
          if (!cancelled && requestId === sessionRequestIdRef.current) {
            setCredits(r);
          }
        })
        .catch(() => {});

      if (!cancelled && requestId === sessionRequestIdRef.current) {
        setLoading(false);
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      void handleSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      void handleSession(session);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [applyPreferences, loadPreferences]);

  const signInWithGoogle = useCallback(async () => {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.href },
    });
  }, []);

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    },
    []
  );

  const signUpWithEmail = useCallback(
    async (email: string, password: string, displayName: string) => {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { display_name: displayName } },
      });
      if (error) throw error;
    },
    []
  );

  const signOut = useCallback(async () => {
    setApiToken("");
    await supabase.auth.signOut();
    window.location.href = "/";
  }, []);

  const updatePreferences = useCallback(
    async (
      prefs: UserPreferences | ((current: UserPreferences) => UserPreferences)
    ) => {
      const next =
        typeof prefs === "function"
          ? prefs(preferencesRef.current)
          : prefs;
      applyPreferences(next);
      preferencesSaveQueueRef.current = next;
      try {
        await flushPreferenceSaves();
      } catch (error) {
        if (preferencesRef.current === next) {
          applyPreferences(persistedPreferencesRef.current);
        }
        throw error;
      }
    },
    [applyPreferences, flushPreferenceSaves]
  );

  const refreshCredits = useCallback(async () => {
    try {
      const r = await api.get<CreditBalance>("/api/credits/balance");
      setCredits(r);
    } catch {
      // ignore
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        preferences,
        defaultApiKey,
        handle,
        credits,
        refreshCredits,
        signInWithGoogle,
        signInWithEmail,
        signUpWithEmail,
        signOut,
        updatePreferences,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
